import { cn } from '@/lib/cn';

interface RatingBarProps {
  label: string;
  /** Score in [0, 10] */
  value: number;
  className?: string;
}

/**
 * Single labelled rating row used by the deck-check screen.
 * Mirrors the ATK/DEF/SYN/VER/BAL visualisation produced by QBot's
 * `deckCheckCard.ts` Canvas PNG, but rendered as live DOM so the score
 * stays accessible.
 */
export function RatingBar({ label, value, className }: RatingBarProps) {
  const clamped = Math.max(0, Math.min(10, value));
  const pct = (clamped / 10) * 100;
  const color =
    clamped >= 8 ? 'bg-emerald-500'
    : clamped >= 6 ? 'bg-lime-500'
    : clamped >= 4 ? 'bg-amber-500'
    : 'bg-rose-500';

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <span className="w-10 text-xs font-bold uppercase tracking-wider text-fg-muted">{label}</span>
      <div className="flex-1 overflow-hidden rounded-full bg-bg-tertiary">
        <div
          className={cn('h-2 rounded-full transition-[width]', color)}
          style={{ width: `${pct}%` }}
          aria-valuemin={0}
          aria-valuemax={10}
          aria-valuenow={clamped}
          role="progressbar"
        />
      </div>
      <span className="w-8 text-right text-xs font-bold tabular-nums text-fg">{clamped.toFixed(1)}</span>
    </div>
  );
}
